import { useTranslation } from 'react-i18next';
import { cn } from '@resource-kit/ui';
import { Badge } from '@resource-kit/ui/react';
import { GATE_LABEL, STAGE_LABEL, formatBatchDay } from '@features/dispatch';
import { deliveryDayRows, stageChain, type DeliveryDayRow, type StepState } from '../overviewModel';
import { useOverviewBoard } from '../useOverviewBoard';
import { CardStatus, OverviewCard } from './OverviewCard';

const STEP_CLASS = {
  done: 'bg-success',
  current: 'bg-primary',
  pending: 'bg-muted',
} as const satisfies Record<StepState, string>;

function DayRow({ row }: { row: DeliveryDayRow }) {
  const { t, i18n } = useTranslation();
  const count =
    row.total === 0
      ? ''
      : row.delivered > 0
        ? t('overview.days.delivered', { delivered: row.delivered, total: row.total })
        : t('overview.days.total', { count: row.total });
  const gate = row.nextGate ? t(GATE_LABEL[row.nextGate.kind], { at: row.nextGate.localTime }) : '';
  const sub = [count, gate].filter(Boolean).join(' · ');
  return (
    <div
      data-testid="overview-day-row"
      className="grid grid-cols-[7rem_1fr_auto] items-center gap-3 border-b py-2 last:border-b-0"
    >
      <div>
        <div className="font-medium">{formatBatchDay(row.date, i18n.language)}</div>
        <div className="flex gap-1 text-xs text-muted-foreground">
          {row.isToday ? <span>{t('dispatch.board.today')}</span> : null}
          {row.isTomorrow ? <span>{t('overview.days.tomorrow')}</span> : null}
          {row.restDay ? <span>{t('dispatch.board.restDayBadge')}</span> : null}
        </div>
      </div>
      <div className="flex gap-1" aria-hidden="true">
        {row.steps.map((step) => (
          <span key={step.stage} className={cn('h-1.5 flex-1 rounded', STEP_CLASS[step.state])} />
        ))}
      </div>
      <div className="text-right">
        <Badge variant={row.badge === 'delivered' ? 'success' : 'secondary'}>
          {row.badge ? t(STAGE_LABEL[row.badge]) : t('overview.days.empty')}
        </Badge>
        {sub ? <div className="text-xs text-muted-foreground">{sub}</div> : null}
      </div>
    </div>
  );
}

/** Today and the next two days with delivery jobs, each as a stage strip. */
export function DeliveryDaysCard({ today, className }: { today: string; className?: string }) {
  const { t } = useTranslation();
  const { board, columns } = useOverviewBoard(today);
  return (
    <OverviewCard
      title={t('overview.days.title')}
      subtitle={
        <span data-testid="overview-days-chain">
          {stageChain(columns)
            .map((stage) => t(STAGE_LABEL[stage]))
            .join(' → ')}
        </span>
      }
      link={{ to: '/dispatch', label: t('overview.days.open') }}
      className={className}
    >
      {board.data ? (
        deliveryDayRows(board.data, today, columns).map((row) => (
          <DayRow key={row.date} row={row} />
        ))
      ) : (
        <CardStatus
          loading={board.isPending}
          label={board.isPending ? t('overview.loading') : t('overview.loadError')}
        />
      )}
    </OverviewCard>
  );
}
